/**
 * Utility functions for dining itinerary date management.
 *
 * Responsibility:
 * - Calculate cruise days and date ranges from cruise metadata
 * - Build itinerary day labels (day number + port or "Day at Sea")
 * - Group days into week filters (7-day chunks)
 * - Format date strings for display and API calls
 */

import { CruiseI } from '@msc-b2c/interfaces/interfaces';
import { managePlurals, replacePlaceholders } from '@msc-b2c/shared-utils';

import { ItineraryDay, WeekGroup } from '@msc-b2c/interfaces/interfaces';

export type { ItineraryDay, WeekGroup };

/**
 * Calculates the number of sailing days from cruise metadata.
 *
 * Primary source is `numberOfSailingDays`; if unavailable/invalid, it falls
 * back to departure/arrival date difference.
 */
export const getCruiseDayCount = (cruise: CruiseI): number => {
  const parsedSailingDays = Number.parseInt(cruise.numberOfSailingDays, 10);

  if (!Number.isNaN(parsedSailingDays) && parsedSailingDays > 0) {
    return parsedSailingDays;
  }

  const departure = new Date(cruise.departureDate);
  const arrival = new Date(cruise.arrivalDate);

  if (Number.isNaN(departure.getTime()) || Number.isNaN(arrival.getTime())) {
    return 0;
  }

  const departureUtc = Date.UTC(
    departure.getUTCFullYear(),
    departure.getUTCMonth(),
    departure.getUTCDate()
  );
  const arrivalUtc = Date.UTC(
    arrival.getUTCFullYear(),
    arrival.getUTCMonth(),
    arrival.getUTCDate()
  );

  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const dayDifference = Math.floor((arrivalUtc - departureUtc) / millisecondsPerDay);

  // Inclusive range (departure day is Day 1).
  return Math.max(1, dayDifference + 1);
};

/**
 * Builds itinerary day objects (1-indexed) from cruise dates.
 *
 * @returns Array of ItineraryDay objects, 1-indexed
 */
export const buildItineraryDays = (
  cruise: CruiseI,
  dayLabel: string,
  dayAtSeaLabel: string,
  itineraryDetails?: { ports?: Array<{ dayNumber: number; name: string; isAtSea: boolean }> }
): ItineraryDay[] => {
  const dayCount = getCruiseDayCount(cruise);
  if (dayCount === 0) return [];

  const startDate = new Date(cruise.departureDate);
  const days: ItineraryDay[] = [];

  for (let dayNumber = 1; dayNumber <= dayCount; dayNumber++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + (dayNumber - 1));

    const isStartDay = dayNumber === 1;
    const isEndDay = dayNumber === dayCount;

    // Check if we have detailed port info; otherwise default to generic labels
    const portInfo = itineraryDetails?.ports?.find((p) => p.dayNumber === dayNumber);

    const portName = portInfo?.name;
    const isAtSea = portInfo?.isAtSea ?? false;

    const label = buildDayLabel(dayNumber, dayLabel, dayAtSeaLabel, portName, isAtSea);

    days.push({
      dayNumber,
      date: date.toISOString().split('T')[0], // ISO date string YYYY-MM-DD
      portName,
      isAtSea,
      isStartDay,
      isEndDay,
      label,
    });
  }

  return days;
};

/**
 * Builds a display label for a single day.
 *
 * Format: "{dayLabel} 1 - Miami" (if at port) or "{dayLabel} 1 - {dayAtSeaLabel}"
 *
 * @param dayNumber - 1-indexed day number
 * @param dayLabel - AEM-authored singular day label
 * @param dayAtSeaLabel - AEM-authored day-at-sea label
 * @param portName - Port name or undefined
 * @param isAtSea - Whether this day is at sea
 * @returns Formatted day label
 */
export const buildDayLabel = (
  dayNumber: number,
  dayLabel: string,
  dayAtSeaLabel: string,
  portName?: string,
  isAtSea?: boolean
): string => {
  const dayPrefix = `${dayLabel} ${dayNumber}`;

  if (isAtSea || !portName) {
    return `${dayPrefix} - ${dayAtSeaLabel}`;
  }

  return `${dayPrefix} - ${portName}`;
};

/**
 * Groups itinerary days into week filters.
 *
 * Splits days into groups of 7. If the total is not a multiple of 7,
 * the last group contains the remainder.
 *
 * Examples:
 *  - 14 days: [Days 1-7], [Days 8-14]
 *  - 10 days: [Days 1-7], [Days 8-10]
 *  - 8 days: [Days 1-7], [Day 8]
 *
 * @param itineraryDays - All itinerary days (from buildItineraryDays)
 * @returns Array of WeekGroup objects
 */
export const groupDaysByWeek = (
  itineraryDays: ItineraryDay[],
  weekFilterLabel: string
): WeekGroup[] => {
  if (itineraryDays.length === 0) return [];

  const WEEK_LENGTH = 7;
  const weeks: WeekGroup[] = [];

  for (let i = 0; i < itineraryDays.length; i += WEEK_LENGTH) {
    const weekDays = itineraryDays.slice(i, Math.min(i + WEEK_LENGTH, itineraryDays.length));

    if (weekDays.length === 0) break;

    const startDay = weekDays[0].dayNumber;
    const endDay = weekDays[weekDays.length - 1].dayNumber;
    const weekNumber = Math.floor(i / WEEK_LENGTH);

    const label = buildWeekLabel(startDay, endDay, weekFilterLabel);

    weeks.push({
      weekNumber,
      label,
      startDayNumber: startDay,
      endDayNumber: endDay,
      days: weekDays,
    });
  }

  return weeks;
};

/**
 * Builds a display label for a week group.
 *
 * Uses a single AEM-authored plural-aware template, for example:
 *  - "{dayLabel} {startDayNumber} || {dayLabel} {startDayNumber} - {endDayNumber}"
 *
 * @param startDayNumber - 1-indexed start day
 * @param endDayNumber - 1-indexed end day
 * @param weekFilterLabel - AEM-authored singular/plural template for week filters
 * @returns Formatted week label
 */
export const buildWeekLabel = (
  startDayNumber: number,
  endDayNumber: number,
  weekFilterLabel: string
): string => {
  return replacePlaceholders(
    managePlurals(startDayNumber === endDayNumber ? 1 : 2, weekFilterLabel),
    {
      startDayNumber: startDayNumber.toString(),
      endDayNumber: endDayNumber.toString(),
    }
  );
};

/**
 * Determines if a cruise is "long" (> 7 days).
 * Long cruises show week filters; short cruises show all days.
 *
 * @param dayCount - Number of sailing days
 * @returns true if dayCount > 7
 */
export const isLongCruise = (dayCount: number): boolean => dayCount > 7;
